import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import "./RequestLink.css";
import axiosInstance from '../../axiosInstance/axiosInstance';
import useAuthForLink from '../../customHooks/useAuthForLink';
import requestTypeMap from '../../common/requestTypeMap';
import PageLoader from '../components/PageLoader/PageLoader';
import ReviewsModal from '../components/Reviews/ReviewsModal';
import { isReviewable } from '../../common/reviewStatus';

// Opens a single request from a link we sent the guest (/requests/:requestId).
// The detail pages read everything from location.state, so this resolves the id
// into that state and then renders the normal detail page in place — the URL
// stays put, which keeps the link shareable and refreshable.
export default function RequestLink() {

    const { requestId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const isAuthenticated = useAuthForLink(location.pathname);

    const [isLoading, setIsLoading] = useState(true);
    const [resolved, setResolved] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    // For Rating
    const [isReviewModal, setIsReviewModal] = useState(false);
    const [requestData, setRequestData] = useState({});

    const buildRequestData = (request, reviewType) => {
        return {
            bookingId: request.booking_id,
            bookedRoomId: request.booked_room_id,
            guestRequestId: request.id,
            hotelId: request.hotel_id,
            requestType: reviewType,
        };
    };

    // Ask for a rating whenever the hotel has delivered and they haven't rated
    // yet. Already rated is left alone: the "view rating" chip on the listing is
    // there for that, and re-opening it on every tap would just be in the way.
    const shouldAskForRating = (request, entry) => {
        if (entry?.rateable === false) {
            return false;
        }
        return isReviewable(request.status) && !request.is_rating_given;
    };

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        let isCancelled = false;

        const resolveRequest = async () => {
            try {
                setIsLoading(true);

                // Clear the previous request first: location.state still holds
                // it, so without this a different id would briefly render the
                // old request's page with the old data.
                setResolved(null);
                setErrorMessage("");
                setIsReviewModal(false);
                setRequestData({});

                const userId = localStorage.getItem("user_id");
                const response = await axiosInstance.get(`/api/v1/users/${userId}/requests`);

                // The raw feed on purpose: Bookings merges Web Checkin rows by
                // booking, which would hide the individual id we were linked to.
                const requests = response?.data?.data || [];
                const request = requests.find(item => item.id === requestId);

                if (isCancelled) {
                    return;
                }

                if (!request) {
                    setErrorMessage("We couldn't find this request on your account. It may have been raised from a different phone number.");
                    return;
                }

                const entry = requestTypeMap[request.type];

                if (!entry) {
                    // No detail page mapped for this type. They can still rate it,
                    // just without the request behind the sheet.
                    if (shouldAskForRating(request, entry)) {
                        setRequestData(buildRequestData(request));
                        setIsReviewModal(true);
                        return;
                    }
                    navigate("/bookings", { replace: true });
                    return;
                }

                const state = await entry.load(request);

                if (isCancelled) {
                    return;
                }

                setResolved({
                    Component: entry.pickComponent ? entry.pickComponent(request) : entry.Component,
                });

                if (shouldAskForRating(request, entry)) {
                    setRequestData(buildRequestData(request, entry.reviewType));
                    setIsReviewModal(true);
                }

                // Hand the detail page the state it would have got from a card tap
                navigate(location.pathname, {
                    state: { ...state, __fromRequestLink: true },
                    replace: true,
                });

            } catch (error) {
                console.log(error);
                if (!isCancelled) {
                    setErrorMessage(error?.response?.data?.message || "We couldn't open this request. Please try again.");
                }
            }
            finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        resolveRequest();

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, requestId]);

    // Only render the detail page once its state is actually in place, otherwise
    // it would bounce itself to /bookings.
    const DetailComponent = resolved?.Component;
    const isDetailReady = DetailComponent && location.state?.__fromRequestLink;

    return (
        <>
            {isLoading && <PageLoader />}

            {errorMessage && (
                <div className='request-link-message'>
                    <h3>We couldn't open this request</h3>
                    <p>{errorMessage}</p>
                    <button onClick={() => navigate("/bookings")}>Go to my requests</button>
                </div>
            )}

            {isDetailReady && <DetailComponent />}

            {isReviewModal && (
                <ReviewsModal
                    isReviewModal={isReviewModal}
                    setIsReviewModal={setIsReviewModal}
                    requestData={requestData}
                    setRequestData={setRequestData}
                    onSubmitted={() => {
                        toast.success("Thanks for your feedback!");
                        if (!isDetailReady) {
                            navigate("/bookings");
                        }
                    }}
                    onClose={() => {
                        // Nothing behind the sheet for this type, so don't leave
                        // them on an empty page
                        if (!isDetailReady) {
                            navigate("/bookings");
                        }
                    }}
                />
            )}
        </>
    );
}
